import { useEffect, useState } from 'react';
import {
  enablePush,
  notificationPermission,
  pushRoleEnabledHere,
  pushSupport,
  pushUnavailableReason,
  syncPushSubscription,
  type PushUnavailableReason,
} from '../../lib/push.js';
import { detectEnvironment, notificationHelp } from '../../lib/platform.js';

const DISMISS_KEY = 'pmr-notify-dismissed-at';
const DISMISS_DENIED_KEY = 'pmr-notify-denied-dismissed-at';
const DISMISS_UNAVAILABLE_KEY = 'pmr-notify-unavailable-dismissed-at';
/** אחרי "לא עכשיו" לא מציקים שוב במשך כמה ימים */
const DISMISS_FOR_MS = 3 * 24 * 60 * 60 * 1000;

function dismissedRecently(key: string): boolean {
  try {
    const at = Number(localStorage.getItem(key));
    return Number.isFinite(at) && at > 0 && Date.now() - at < DISMISS_FOR_MS;
  } catch {
    return false;
  }
}

function rememberDismissed(key: string): void {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch {
    // בלי localStorage — ההודעה תופיע שוב בטעינה הבאה, וזה בסדר
  }
}

const UNAVAILABLE_TEXT: Record<PushUnavailableReason, string> = {
  'in-app-browser':
    '📲 את האפליקציה פתחתם בתוך אפליקציה אחרת (וואטסאפ, אינסטגרם וכד׳). כדי לקבל התראות פתחו אותה בדפדפן עצמו.',
  'old-ios': '🔔 כדי לקבל התראות באייפון/באייפד צריך iOS 16.4 ומעלה (הגדרות ← כללי ← עדכון תוכנה).',
  'unsupported-browser':
    '🔔 הדפדפן הזה לא תומך בהתראות. ב-Chrome, Edge, Firefox או Safari עדכניים — או באפליקציה שהותקנה למסך הבית — הן יעבדו. עד אז האפליקציה מתעדכנת כשהיא פתוחה.',
};

function HelpSteps() {
  const help = notificationHelp(detectEnvironment());
  return (
    <div className="notify-help">
      <strong>{help.heading}</strong>
      <ol>
        {help.steps.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>
    </div>
  );
}

/**
 * בקשת אישור להתראות לשחקן — מוצגת במסך הסוכן, וההרשאה של הדפדפן נבקשת רק בלחיצה על הכפתור.
 * מכשיר שכבר אישר רק מוודא בשקט שהמנוי שלו רשום בשרת (ונשארת קישור קטן "לא מגיעות התראות?" עם הוראות לפי המכשיר:
 * אייפון, סמסונג, שיאומי, מחשב...). כשהמכשיר או הדפדפן לא מאפשרים התראות בכלל — מוסבר למה ומה עושים. באייפון בלי
 * אפליקציה מותקנת ההנחיה היא בהודעת ההתקנה (InstallPrompt).
 */
export function NotifyPrompt() {
  const [support] = useState(pushSupport);
  const [env] = useState(detectEnvironment);
  const [permission, setPermission] = useState(notificationPermission);
  const [enabled, setEnabled] = useState(() => pushRoleEnabledHere('player'));
  const [dismissed, setDismissed] = useState(() => dismissedRecently(DISMISS_KEY));
  const [deniedDismissed, setDeniedDismissed] = useState(() => dismissedRecently(DISMISS_DENIED_KEY));
  const [unavailableDismissed, setUnavailableDismissed] = useState(() => dismissedRecently(DISMISS_UNAVAILABLE_KEY));
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    void syncPushSubscription('player');
  }, []);

  useEffect(() => {
    if (message?.kind !== 'ok') return;
    const id = window.setTimeout(() => setMessage(null), 4_000);
    return () => window.clearTimeout(id);
  }, [message]);

  if (support !== 'supported') {
    const reason = pushUnavailableReason(env, support);
    if (!reason || unavailableDismissed) return null;
    return (
      <div className="notify-prompt term-panel notify-prompt--stack" role="complementary" aria-label="התראות">
        <span className="term-text">{UNAVAILABLE_TEXT[reason]}</span>
        {reason === 'in-app-browser' && <HelpSteps />}
        <div className="notify-prompt__actions">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              rememberDismissed(DISMISS_UNAVAILABLE_KEY);
              setUnavailableDismissed(true);
            }}
          >
            הבנתי
          </button>
        </div>
      </div>
    );
  }

  const enable = async () => {
    setBusy(true);
    setMessage(null);
    const result = await enablePush('player');
    setBusy(false);
    setPermission(notificationPermission());
    if (result.ok) {
      setEnabled(true);
      setMessage({ kind: 'ok', text: '✓ ההתראות הופעלו — תקבלו הודעה על משימה חדשה גם כשהטלפון נעול' });
    } else {
      setMessage({ kind: 'error', text: `⚠️ ${result.error}` });
    }
  };

  const dismiss = () => {
    rememberDismissed(DISMISS_KEY);
    setDismissed(true);
  };

  if (message) {
    return (
      <div className={`notify-prompt term-panel notify-prompt--${message.kind}`} role="status">
        <span className="term-text">{message.text}</span>
      </div>
    );
  }

  if (permission === 'denied') {
    if (deniedDismissed) return null;
    return (
      <div className="notify-prompt term-panel notify-prompt--stack" role="complementary" aria-label="התראות חסומות">
        <span className="term-text">
          🔕 ההתראות חסומות בדפדפן הזה, ולכן לא תקבלו הודעה על משימה חדשה כשהמסך נעול. אפשר לאפשר אותן שוב בהגדרות האתר.
        </span>
        {helpOpen ? (
          <HelpSteps />
        ) : (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setHelpOpen(true)}>
            איך מאפשרים?
          </button>
        )}
        <div className="notify-prompt__actions">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              rememberDismissed(DISMISS_DENIED_KEY);
              setDeniedDismissed(true);
            }}
          >
            סגור
          </button>
        </div>
      </div>
    );
  }

  if (enabled) {
    return (
      <div className="notify-help-wrap">
        <button type="button" className="notify-help-link" aria-expanded={helpOpen} onClick={() => setHelpOpen((v) => !v)}>
          🔔 התראות מופעלות · לא מגיעות התראות?
        </button>
        {helpOpen && <HelpSteps />}
      </div>
    );
  }
  if (dismissed) return null;

  return (
    <div className="notify-prompt term-panel" role="complementary" aria-label="התראות">
      <span className="term-text">🔔 לקבל התראה כשנפתחת משימה או מגיעה הודעה — גם כשהטלפון נעול?</span>
      <div className="notify-prompt__actions">
        <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => void enable()}>
          {busy ? 'מפעיל...' : 'הפעל התראות'}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={dismiss}>
          לא עכשיו
        </button>
      </div>
    </div>
  );
}
